import FormHelper from '../components/editor/form_builder/FormHelper'
import contract_fields_csi from '../configuration_data/contract_fields_csi.json'
import { IContract } from '../definitions/Contract'
import { IContractSchema } from '../definitions/ContractSchema'
import { IFormField } from '../definitions/FormField'
import { IFormGroup } from '../definitions/FormGroup'
import { NestedKeyOf } from '../definitions/Utility'
import Helper from '../helpers/Helper'
import SimilarityManager from './SimilarityManager'
class ContractsManagerClass {
    private fieldInfoMap: { [key: string]: FieldInfo } = {}

    constructor() {
        this.buildFieldInfoMap(contract_fields_csi)
    }

    private buildFieldInfoMap(fields: any[]) {
        fields.forEach(category => {
            category.fields.forEach((field: any) => {
                this.fieldInfoMap[field.key] = {
                    label: field.label,
                    description: field.description || ''
                }
                if (field.childFields) {
                    this.buildFieldInfoMap([{ fields: field.childFields }])
                }
                if (field.objectFields) {
                    this.buildFieldInfoMap([{ fields: field.objectFields }])
                }
            })
        })
    }

    getCSIFields = (): IFormGroup[] => {
        return contract_fields_csi as IFormGroup[]
    }

    getFieldsFromTree = (): string[] => {
        const extractFields = (fields: any[]): string[] => {
            let result: string[] = []
            fields.forEach(field => {
                if (field.key) {
                    result.push(field.key)
                }
                if (field.childFields) {
                    result = result.concat(extractFields(field.childFields))
                }
                if (field.objectFields) {
                    result = result.concat(extractFields(field.objectFields))
                }
            })
            return result
        }

        const fieldsFromTree = contract_fields_csi.flatMap(category => extractFields(category.fields))
        return fieldsFromTree
    }

    getFieldLabel(key: string): string {
        return this.fieldInfoMap[key]?.label || key
    }

    getFieldDescription(key: string): string {
        return this.fieldInfoMap[key]?.description || ''
    }

    similarity = (c1: IContract, c2: IContract, fields: CSIFieldOption[]): number => {
        const scores = fields.map(field => {
            const val1 = Helper.getNestedValue(c1, field)
            const val2 = Helper.getNestedValue(c2, field)

            return SimilarityManager.calculateCosineSimilarity(val1, val2)
        })

        return scores.reduce((sum, score) => sum + score, 0) / scores.length
    }

    similarityFields = (c1: IContract, c2: IContract, fields: CSIFieldOption[]): string[] => {
        return fields.filter(field => {
            const val1 = Helper.getNestedValue(c1, field)
            const val2 = Helper.getNestedValue(c2, field)

            return SimilarityManager.calculateCosineSimilarity(val1, val2) > 0.8
        })
    }

    fieldSimilarities = (
        c1: IContract,
        c2: IContract,
        fields: NestedKeyOf<IContract>[]
    ): { field: string, score: number }[] => {
        return fields.map(field => {
            const val1 = Helper.getNestedValue(c1, field)
            const val2 = Helper.getNestedValue(c2, field)

            return {
                field,
                score: SimilarityManager.calculateCosineSimilarity(val1, val2)
            }
        })
    }

    fieldPercentages = (
        c1: IContract,
        c2: IContract,
        fields: NestedKeyOf<IContract>[]
    ): { field: string, percentage: number }[] => {
        return fields.map(field => {
            const val1 = Helper.getNestedValue(c1, field)
            const val2 = Helper.getNestedValue(c2, field)

            return {
                field,
                percentage: SimilarityManager.calculateCosineSimilarity(val1, val2) * 100
            }
        })
    }

    getAllFields = (): NestedKeyOf<IContract>[] => {
        return Object.keys(this.fieldInfoMap) as NestedKeyOf<IContract>[]
    }

    countAllFields = (): number => {
        return this.getFieldsFromTree().length
    }

    crossSimilarities = (contracts: IContract[], fieldsToInclude?: CSIFieldOption[]): CSimilarity[] => {
        if (!contracts || contracts.length < 1) return []

        const fields = fieldsToInclude || this.getAllFields()
        const result: CSimilarity[] = []

        for (const c1 of contracts) {
            const item: CSimilarity = { from: c1, tos: [], avg: 0 }

            for (const c2 of contracts) {
                if (c2.uuid !== c1.uuid) {
                    item.tos.push({
                        uuid: c2.uuid,
                        name: c2.name,
                        score: this.similarity(c1, c2, fields),
                        commonFields: this.similarityFields(c1, c2, fields)
                    })
                }
            }

            item.tos.sort((a, b) => b.score - a.score)
            item.avg = item.tos.map(x => x.score).reduce((p, c) => p + c, 0) / item.tos.length
            result.push(item)
        }

        return result
    }

    aiValuesMatch = (type: IFormField['type'], formValue: any, aiValue: any): boolean => {
        if (aiValue == null) return false

        if (type === 'system-config') {
            if (!formValue) return false
            const ai = String(aiValue).trim().toLowerCase()
            return (
                String(formValue.name ?? '').trim().toLowerCase() === ai
                || String(formValue.uuid ?? '').trim().toLowerCase() === ai
            )
        }

        if (type === 'multi-system-config') {
            if (!Array.isArray(formValue) || !formValue.length) return false
            const aiList = Array.isArray(aiValue)
                ? aiValue.map(v => String(v).trim().toLowerCase())
                : String(aiValue).split(/[;,]/).map(v => v.trim().toLowerCase())
            const namesOrIds = formValue.map(o =>
                String(o.name ?? o.uuid ?? '').trim().toLowerCase()
            )
            return aiList.every(v => namesOrIds.includes(v))
        }

        if (type === 'number') {
            return String(formValue).trim().toLowerCase() === String(aiValue).trim().toLowerCase()
        }

        return Helper.deepEquals(formValue, aiValue)
    }

    removeIndex(path: string): string {
        return path.replace(/\[\d+\]/g, '[]')
            .replace(/\[\]/g, '')
            .trim()
    }

    validateContractRequiredFields = (contract: IContract, schema: IContractSchema): ValidationContract => {
        const errors: ValidationError[] = []

        const validateRec = (field: IFormField, currentItem: any, basePath: string, step: IFormGroup, listIndices: number[], depth: number) => {
            let fieldKey: string
            let fullPath: string

            if (!basePath && field.key.includes('.')) {
                fieldKey = field.key
                fullPath = field.key
            } else {
                fieldKey = field.key.split('.').pop()
                fullPath = basePath ? `${basePath}.${fieldKey}` : field.key
            }

            const currentIndex = listIndices.length > 0 ? listIndices[listIndices.length - 1] : undefined

            let value: any
            if (!basePath && field.key.includes('.')) {
                value = Helper.getNestedValue(currentItem, field.key)
            } else {
                value = FormHelper.getValue(currentItem, fieldKey, currentIndex ?? 0, step)
            }

            if (field.type === 'list') {
                const list = value ?? []
                if (field.validation?.isRequired && (!Array.isArray(list) || list.length === 0)) {
                    errors.push({
                        path: fullPath,
                        indexes: [...listIndices],
                        message: `${field.label} must have at least one item`,
                        stepLabel: step.label,
                        fieldLabel: field.label
                    })
                }

                if (Array.isArray(list) && list.length > 0 && field.childFields) {
                    list.forEach((listItem, i) => {
                        field.childFields.forEach(childField => {
                            validateRec(childField, listItem, fullPath, step, [...listIndices, i], depth + 1)
                        })
                    })
                }
                return
            }

            if (field.type === 'object') {
                const objectValue = value ?? {}
                if (field.objectFields) {
                    field.objectFields.forEach(objField => {
                        validateRec(objField, objectValue, fullPath, step, listIndices, depth + 1)
                    })
                }
                return
            }

            if (field.validation?.isRequired) {
                const { isError, helperText } = FormHelper.validateField(
                    field,
                    value,
                    currentItem,
                    currentIndex ?? 0,
                    step
                )

                if (isError && helperText) {
                    errors.push({
                        path: fullPath,
                        indexes: [...listIndices],
                        message: helperText,
                        stepLabel: step.label,
                        fieldLabel: field.label
                    })
                }
            }
        }

        schema.steps.forEach(step => {
            step.fields.forEach(field => {
                validateRec(field, contract, '', step, [], 0)
            })
        })

        return {
            isValid: errors.length === 0,
            errors
        }
    }

    buildDisplayPath = (error: ValidationError): string => {
        const parts = error.path.split('.')

        if (error.indexes.length === 0) {
            return parts.join(' → ')
        }

        const result: string[] = []
        let indicesUsed = 0

        for (const part of parts) {
            if (indicesUsed < error.indexes.length) {
                result.push(`${part} #${error.indexes[indicesUsed] + 1}`)
                indicesUsed++
            } else {
                result.push(part)
            }
        }

        return result.join(' → ')
    }
}

const ContractsManager = new ContractsManagerClass()
export default ContractsManager

export type CSimilarity = {
    from: IContract
    tos: {
        uuid: string
        name: string
        score: number
        commonFields: string[]
    }[]
    avg: number
}

export type FieldInfo = {
    label: string
    description?: string
}

export interface ValidationError {
    index?: number
    indexes?: number[]
    message: string
    stepLabel: string
    fieldLabel: string
    listIndex?: number
    path: string
}

export interface ValidationContract {
    errors: ValidationError[]
    isValid: boolean
}

export type CSIFieldOption = NestedKeyOf<IContract> | 'all'
